import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useAnimationFrame } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/Home.css';

const profileImageUrl = 'https://gravatar.com/avatar/6282b274b57537fa4d30a7334fd127b3?size=256';

// Optimized Tool Component
const Tool = ({ tool, mouseX, mouseY }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useMotionValue(0);

  const state = useRef({
    angle: tool.angle,
    currentOrbitRadius: tool.currentOrbitRadius,
    radialVelocity: 0,
    rotation: tool.rotation
  });

  useAnimationFrame(() => {
    const s = state.current;
    
    s.angle += tool.velocity;

    const springStrength = 0.01;
    const damping = 0.1;
    const springForce = (tool.targetOrbitRadius - s.currentOrbitRadius) * springStrength;
    const dampingForce = -s.radialVelocity * damping;
    
    s.radialVelocity += (springForce + dampingForce);
    s.currentOrbitRadius += s.radialVelocity;
    s.rotation = (s.rotation + tool.rotationSpeed) % 360;

    const currentMouseX = mouseX.get();
    const currentMouseY = mouseY.get();
    
    x.set(currentMouseX + s.currentOrbitRadius * Math.cos(s.angle));
    y.set(currentMouseY + s.currentOrbitRadius * Math.sin(s.angle));
    rotate.set(s.rotation);
  });

  return (
    <motion.div
      className="tool"
      style={{
        position: 'absolute',
        x,
        y,
        rotate,
        fontSize: `${tool.size}px`,
        zIndex: 5,
        pointerEvents: 'none',
        textShadow: '0 0 5px rgba(255, 204, 0, 0.7)'
      }}
    >
      {tool.type}
    </motion.div>
  );
};

const Home = () => {
  const { t } = useTranslation();
  const [tools, setTools] = useState([]);
  const homeRef = useRef(null);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  const smoothMouseX = useSpring(mouseX, { stiffness: 500, damping: 50 });
  const smoothMouseY = useSpring(mouseY, { stiffness: 500, damping: 50 });
  
  const backgroundGradient = useTransform(
    [mouseX, mouseY],
    ([x, y]) => `radial-gradient(circle 200px at ${x}px ${y}px, rgba(40, 40, 40, 0.8) 0%, rgba(20, 20, 20, 0.95) 70%)`
  );

  useEffect(() => {
    const initialTools = Array.from({ length: 10 }, (_, index) => {
      const toolTypes = ['🔧', '🔨', '🪛', '⚙️', '🔩', '🧰', '🗜️', '🔌', '🔋'];
      return {
        id: index,
        type: toolTypes[Math.floor(Math.random() * toolTypes.length)],
        angle: Math.random() * 2 * Math.PI,
        targetOrbitRadius: Math.random() * 100 + 50,
        currentOrbitRadius: Math.random() * 100 + 50,
        velocity: (Math.random() - 0.5) * 0.05,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        size: Math.random() * 20 + 20
      };
    });
    setTools(initialTools);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (homeRef.current) {
        const rect = homeRef.current.getBoundingClientRect();
        // Client coordinates relative to the home section
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        mouseX.set(x);
        mouseY.set(y);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  return (
    <section id="home" className="home" ref={homeRef}>
      <motion.div
        className="hollow-background"
        style={{ background: backgroundGradient }}
      />

      <div className="animated-background">
        <div className="grid-lines">
          {Array.from({ length: 10 }).map((_, index) => (
            <div key={`h-line-${index}`} className="grid-line horizontal" style={{ top: `${index * 10}%` }} />
          ))}
          {Array.from({ length: 10 }).map((_, index) => (
            <div key={`v-line-${index}`} className="grid-line vertical" style={{ left: `${index * 10}%` }} />
          ))}
        </div>

        <div className="maintenance-tools" aria-hidden="true">
          {tools.map((tool) => (
            <Tool key={tool.id} tool={tool} mouseX={mouseX} mouseY={mouseY} />
          ))}
        </div>
      </div>

      <div className="content-container">
        <motion.div
          className="profile-section"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="profile-image-container">
            <img
              src={profileImageUrl}
              alt="Jonathan LLAMAS"
              className="profile-image"
            />
          </div>

          <div className="bio-container">
            <motion.h2
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Jonathan LLAMAS
            </motion.h2>

            <motion.h3
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="job-title"
            >
              <center>{t('home.role')}</center>
            </motion.h3>

            <motion.p
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="bio-text"
            >
              {t('home.bio1')}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.9 }}
              className="bio-text"
            >
              {t('home.bio2')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.1 }}
              className="cv-button-container"
            >
              <a
                href={`/CV_LLAMAS_JONATHAN.pdf?v=${process.env.REACT_APP_BUILD_ID}`}
                download="CV_LLAMAS_JONATHAN.pdf"
                className="cv-button"
                aria-label={t('appointment.downloadCV')}
              >
                <FontAwesomeIcon icon={faFileDownload} />
                {t('appointment.downloadCV')}
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="mouse-follower"
        style={{
          x: smoothMouseX,
          y: smoothMouseY,
          translateX: "-50%",
          translateY: "-50%",
          left: 0,
          top: 0
        }}
      />
    </section>
  );
};

export default Home;